/** O CERERE ȚINUTĂ CA STARE: răspunsul unei citiri asincrone, cu cheia ei.
 *
 *  Răspunsul se aplică după STAREA cererii, nu după curățarea efectului. Un
 *  efect care își schimbă propria stare («se încarcă») se rescrie imediat, și
 *  curățarea lui ar rula înainte să vină răspunsul: legat de ea, răspunsul era
 *  aruncat de fiecare dată, iar ecranul rămânea pe «se citește…» la nesfârșit.
 *  Aici cererea e o stare cu cheia ei, și răspunsul intră numai dacă starea e
 *  tot «se încarcă» pe aceeași cheie: cheia luată (`null`) și dată înapoi cât
 *  răspunsul e pe drum nu cere a doua oară și nu-l pierde, altă cheie e altă
 *  cerere, cu răspunsul ei. Un răspuns care nu se poate citi lasă starea pe
 *  «eroare», o dată — nu se reîncearcă singur.
 */
import { useEffect, useRef, useState } from "react";

export type StareCerere = "nimic" | "se_incarca" | "eroare";
export type Cerut<T> = { val: T | null; stare: StareCerere };
type Cerere<T> = Cerut<T> & { cheie: string | null };
const nimic = <T>(cheie: string | null): Cerere<T> => ({ cheie, val: null, stare: "nimic" });

/** Valoarea cerută pentru `cheie` prin `cere`, o singură dată pe cheie;
 *  nimic cât cheia e `null`. */
export function useCerut<T>(cheie: string | null, cere: () => Promise<T>): Cerut<T> {
  const [cerere, setCerere] = useState<Cerere<T>>(() => nimic<T>(cheie));
  const cereAcum = useRef(cere);
  cereAcum.current = cere;
  // valoarea ținută e a altei chei: pentru cheia asta nu e nimic
  const actuala = cerere.cheie === cheie ? cerere : nimic<T>(cheie);
  useEffect(() => {
    if (cheie === null || actuala.val !== null || actuala.stare !== "nimic") return;
    setCerere({ cheie, val: null, stare: "se_incarca" });
    const inZbor = (c: Cerere<T>) => c.cheie === cheie && c.stare === "se_incarca";
    Promise.resolve()
      .then(() => cereAcum.current())
      .then((val) => setCerere((c) => (inZbor(c) ? { cheie, val, stare: "nimic" } : c)))
      .catch(() => setCerere((c) => (inZbor(c) ? { cheie, val: null, stare: "eroare" } : c)));
  }, [cheie, actuala.val, actuala.stare]);
  return { val: actuala.val, stare: actuala.stare };
}
